import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Clock, Square } from 'lucide-react'
import { Button } from '../ui/button'
import useCommentStore from '../../store/useCommentStore'
import { formatElapsed } from '../../utils/timeFormat'

export default function ActiveTimerBadge() {
  const navigate = useNavigate()
  const { activeTimer, getElapsedSeconds, stopTaskTimer, fetchActiveTimer } = useCommentStore()
  const [elapsed, setElapsed] = useState('00:00:00')

  // Fetch active timer on mount
  useEffect(() => {
    fetchActiveTimer()
  }, [fetchActiveTimer])

  // Update elapsed time every second
  useEffect(() => {
    if (!activeTimer) {
      setElapsed('00:00:00')
      return
    }

    const updateElapsed = () => {
      setElapsed(formatElapsed(getElapsedSeconds()))
    }

    updateElapsed()
    const interval = setInterval(updateElapsed, 1000)
    return () => clearInterval(interval)
  }, [activeTimer, getElapsedSeconds])

  if (!activeTimer) return null

  const handleClick = () => {
    // Navigate to the task
    navigate(`/projects/${activeTimer.projectId}/tasks?taskId=${activeTimer.entry.taskId}`)
  }

  const handleStop = async (e) => {
    e.stopPropagation()
    try {
      await stopTaskTimer()
    } catch (error) {
      console.error('Failed to stop timer:', error)
    }
  }

  const taskTitle = activeTimer.task?.title || 'Task'
  const truncatedTitle = taskTitle.length > 20 ? taskTitle.slice(0, 20) + '...' : taskTitle

  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 bg-green-100 dark:bg-green-900 border border-green-300 dark:border-green-700 cursor-pointer hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
      onClick={handleClick}
      data-testid="active-timer-badge"
    >
      <Clock className="h-4 w-4 text-green-600 dark:text-green-400 animate-pulse" />
      <div className="flex flex-col">
        <span className="text-xs text-green-700 dark:text-green-300 truncate max-w-[150px]">
          {truncatedTitle}
        </span>
        <span className="font-mono text-sm font-medium text-green-800 dark:text-green-200" data-testid="timer-elapsed">
          {elapsed}
        </span>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 hover:bg-red-100 dark:hover:bg-red-900"
        onClick={handleStop}
        data-testid="stop-timer-button"
      >
        <Square className="h-3 w-3 text-red-600" />
      </Button>
    </div>
  )
}
